import React, { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import type { DropdownProps, Pallete } from './types'

import { fetcher } from '@/components/youtube/utils/fetcher'

// TODO put in context
const apiUrl = 'https://ilya1.com/api'

export interface DropdownAddTagProps {
  value: string[]
  options: string[]
  onSelect: (value: string[]) => void
  onDelete: () => void
}

export function DropdownAddTag({ value, options, onSelect, onDelete }: DropdownAddTagProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selected, setSelected] = useState<string[]>(value)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev)
  }
  const { data: palletes = [], error: errorPalletes = {} } = useSWR<Pallete[]>(`${apiUrl}/palettes`, fetcher)

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Synchronize internal state with external value when it changes
  useEffect(() => {
    setSelected(value)
  }, [value])

  const handleSelect = (tag: string) => {
    const updatedSelection = selected.includes(tag)
      ? selected.filter((t) => t !== tag) // Remove if already selected
      : [...selected, tag] // Add if not selected

    setSelected(updatedSelection) // Update internal state
    onSelect(updatedSelection) // Notify parent component
  }

  // Calculate dynamic positioning to prevent overflow
  const getDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const dropdownWidth = 480 // set the dropdown width explicitly

      const isOutOfScreenRight = rect.right + dropdownWidth > windowWidth
      const isOutOfScreenLeft = rect.left < 0

      let positionClass = 'right-0'
      if (isOutOfScreenRight) {
        positionClass = 'left-0'
      } else if (isOutOfScreenLeft) {
        positionClass = 'right-0'
      }

      return positionClass
    }
    return 'right-0'
  }

  return (
    <div ref={dropdownRef} className="relative inline-block text-left">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
          toggleDropdown()
        }}
        className="text-sm m-0 p-0"
      >
        ...
      </a>
      {isOpen && (
        <div
          className={`origin-top-right absolute mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 w-[480px] ${getDropdownPosition()}`}
        >
          <div className="py-1">
            {options.map((tag, index) => {
              const isSelected = selected.includes(tag)
              const palette = palletes[index % palletes.length] || {}
              const styleClass = isSelected ? `${palette.text_class || ''} ${palette.bg_class || ''}` : ''

              return (
                <a
                  href="#"
                  key={tag}
                  className={`px-2 py-1 text-sm text-gray-700 ${styleClass}`}
                  role="menuitem"
                  style={{
                    display: 'inline-block',
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    handleSelect(tag) // Update selection state
                  }}
                >
                  {isSelected ? `✓ ${tag}` : tag}
                </a>
              )
            })}
          </div>
          <div className="p-2">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                // confirm
                if (window.confirm('Are you sure you want to delete?')) {
                  onDelete()
                }
              }}
              className="text-red-600"
            >
              Delete?
            </a>{' '}
          </div>
        </div>
      )}
    </div>
  )
}
